import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Landmark, BadgeCheck, Scale } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Loader, fmt } from "@/components/shared";
import { PlansBackdrop, PlansHero, GlowCta } from "@/components/PlanVisuals";
import { AvailabilityRing, ShareStat, Reveal } from "@/components/ShareVisuals";

const steps = [
  [Landmark, "Choose quantity", "Select any number of shares — from 1 upward — limited only by the shares actually available."],
  [BadgeCheck, "Pay from balance or deposit", "Buy instantly from your available balance or send USDT with the dedicated Deposit for Shares option."],
  [ShieldCheck, "Manual verification", "Every purchase and deposit is reviewed by the platform before shares are confirmed in your account."],
  [Scale, "Transparent ownership", "Track confirmed shares, purchase value and full history in the My Shares section of your dashboard."],
];

export default function SharesPublic() {
  const [info, setInfo] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get("/shares/info").then((r) => setInfo(r.data)).catch(() => setInfo(false)); }, []);
  const cta = user ? "/shares-portal" : "/signup";
  return (
    <PublicLayout>
      <section className="relative overflow-hidden" data-testid="public-shares">
        <PlansBackdrop />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20">
          <PlansHero eyebrow="Company Shares" title="Become a Shareholder in AI TECHNOLOGY"
            subtitle={`Each company share is priced at ${info ? fmt(info.share_price) : "100.00"} USDT. Purchase any quantity subject to available inventory. Shares are confirmed only after verification and do not carry guaranteed returns.`} />
          {info === null ? <Loader /> : info === false ? null : (
            <>
              <Reveal className="plan-card relative rounded-2xl p-7 sm:p-9 bg-[#0B1224]/80 backdrop-blur-xl border border-cyan-500/20 max-w-4xl mx-auto grid md:grid-cols-[auto_1fr] gap-8 items-center">
                <span className="plan-card-border" aria-hidden="true" />
                <AvailabilityRing available={info.available_shares} total={info.total_shares} size={200} />
                <div>
                  <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">Current Offering</p>
                  <div className="mt-3 flex items-end gap-2">
                    <span className="font-mono text-4xl sm:text-5xl font-bold text-white" data-testid="public-share-price">{fmt(info.share_price)}</span>
                    <span className="text-slate-400 mb-1.5">USDT / share</span>
                  </div>
                  <div className="mt-6 grid grid-cols-3 gap-3">
                    <ShareStat label="Available" value={info.available_shares} testId="public-share-available" accent="text-cyan-300" />
                    <ShareStat label="Sold" value={info.sold_shares} testId="public-share-sold" />
                    <ShareStat label="Total" value={info.total_shares} testId="public-share-total" />
                  </div>
                  <div className="mt-6 max-w-xs"><GlowCta as={Link} to={cta} testId="public-share-cta">{user ? "Buy Shares" : "Create Account to Buy"}</GlowCta></div>
                </div>
              </Reveal>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-16">
                {steps.map(([Icon, t, d], i) => (
                  <Reveal key={t} delay={i * 0.08} className="glass glass-hover rounded-2xl p-6">
                    <div className="w-11 h-11 rounded-xl bg-cyan-500/15 flex items-center justify-center text-cyan-300 share-icon-glow"><Icon className="w-5 h-5" /></div>
                    <h3 className="font-heading text-base text-white mt-4">{t}</h3>
                    <p className="text-sm text-slate-400 mt-2 leading-relaxed">{d}</p>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={0.1} className="glass rounded-2xl p-6 sm:p-8 mt-12 max-w-3xl mx-auto">
                <h3 className="font-heading text-lg text-white mb-4">Pricing Examples</h3>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[1, 5, 10, 100, 500].map((n) => (
                    <div key={n} className="rounded-xl bg-slate-900/60 border border-cyan-500/10 p-4 text-center">
                      <p className="font-mono text-lg text-white">{n}</p><p className="text-[10px] uppercase tracking-wider text-slate-500">{n === 1 ? "share" : "shares"}</p>
                      <p className="font-mono text-sm text-cyan-300 mt-2">{fmt(n * info.share_price)}</p><p className="text-[10px] text-slate-500">USDT</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-5">Total = Number of Shares × Current Share Price. Price and inventory are set by the platform and may change. Company shares are not a guaranteed-return or risk-free product. Share issuance is subject to verification, KYC/AML requirements and applicable regulations. See the <Link to="/risk-disclosure" className="text-cyan-400 hover:underline">Risk Disclosure</Link> and <Link to="/terms" className="text-cyan-400 hover:underline">Terms</Link>.</p>
              </Reveal>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
